// SPDX-License-Identifier: AGPL-3.0-or-later
// Pure recognition model for assistant replies that describe a daily schedule.

export interface AssistantScheduleEvent {
	start: string;
	end: string;
	title: string;
	meta: string;
}

export interface AssistantScheduleGap {
	start: string;
	end: string;
}

export interface AssistantMeetingSuggestion {
	title: string;
	people: string;
	reason: string;
	duration: string;
	urgent: boolean;
}

export interface AssistantScheduleReply {
	dateLabel: string;
	events: Array<AssistantScheduleEvent>;
	gaps: Array<AssistantScheduleGap>;
	suggestion: AssistantMeetingSuggestion | null;
	proposedStart: string | null;
}

const EVENT_RANGE = /^(\d{1,2}:\d{2})\s*[–—-]\s*(\d{1,2}:\d{2})\s*(?:[–—-]|·|:)\s*(.+)$/;
const ANY_RANGE = /(\d{1,2}:\d{2})\s*[–—-]\s*(\d{1,2}:\d{2})/g;

function plain(value: string): string {
	return value
		.replace(/^\s*(?:[-*+]\s+|\d+[.)]\s+)/, '')
		.replace(/\*\*|__/g, '')
		.trim();
}

export function clockMinutes(value: string): number {
	const [hours, minutes] = value.split(':').map(Number);
	return hours * 60 + minutes;
}

export function rangeMinutes(start: string, end: string): number {
	return Math.max(0, clockMinutes(end) - clockMinutes(start));
}

export function readableDuration(minutes: number): string {
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (hours === 0) return `${rest}m`;
	if (rest === 0) return `${hours}h`;
	return `${hours}h ${rest}m`;
}

function dateFromHeading(value: string): string | null {
	const parenthetical = value.match(/\(([^()]+)\)\s*:?$/);
	if (parenthetical) return plain(parenthetical[1]);
	const afterSchedule = value.match(/schedule\s+(?:for\s+)?(.+?)\s*:?$/i);
	if (!afterSchedule) return null;
	const label = plain(afterSchedule[1]).replace(/^is\s+/i, '');
	return /^(?:looks like|currently|is)$/i.test(label) ? null : label;
}

function suggestionFromText(value: string): AssistantMeetingSuggestion | null {
	let text = value
		.replace(/^\s*(?:suggestion(?: for a meeting to add)?|suggested meeting|meeting to add)\s*:\s*/i, '')
		.trim();
	if (!text) return null;

	const boldTitle = text.match(/^\*\*(.+?)\*\*/s);
	let rawTitle = '';
	if (boldTitle) {
		rawTitle = boldTitle[1];
		text = text.slice(boldTitle[0].length).trim();
	} else {
		const firstRawLine = text.split('\n')[0] ?? '';
		const firstLine = plain(firstRawLine);
		const titleEnd = firstLine.search(/\s+(?:with\s+|[—–]\s+)/i);
		rawTitle = titleEnd > 0 ? firstLine.slice(0, titleEnd) : firstLine;
		const rawTitleStart = Math.max(0, firstRawLine.indexOf(rawTitle));
		text = text.slice(rawTitleStart + rawTitle.length).trim();
	}

	const urgent = /^urgent\b/i.test(plain(rawTitle));
	const title = plain(rawTitle)
		.replace(/^urgent\s*[-–—:]\s*/i, '')
		.trim();
	if (!title) return null;

	text = text
		.replace(/\bWant me to schedule it\?\s*/gi, '')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
	let people = '';
	const peopleMatch = text.match(/^with\s+(.+?)\s+[—–]\s+([\s\S]*)$/i);
	if (peopleMatch) {
		people = plain(peopleMatch[1]);
		text = peopleMatch[2].trim();
	}
	const durationMatch = text.match(/\b(\d{1,3}\s*[–—-]\s*\d{1,3}\s*(?:min(?:ute)?s?)|\d{1,3}\s*min(?:ute)?s?)\b/i);
	return {
		title,
		people,
		reason: plain(text),
		duration: durationMatch ? plain(durationMatch[1]) : '',
		urgent,
	};
}

/**
 * Conservative by design: an ordinary numbered answer must never suddenly
 * become a calendar. Two time-ranged events plus a schedule cue are required.
 */
export function parseAssistantScheduleReply(content: string): AssistantScheduleReply | null {
	const lines = content.split(/\r?\n/);
	const events: Array<AssistantScheduleEvent> = [];
	const gaps: Array<AssistantScheduleGap> = [];
	let dateLabel = '';
	let sawScheduleCue = false;
	let suggestionIndex = -1;

	for (let index = 0; index < lines.length; index += 1) {
		const raw = lines[index] ?? '';
		const line = plain(raw);
		if (!line) continue;
		if (/\b(schedule|calendar|meetings?)\b/i.test(line)) {
			sawScheduleCue = true;
			dateLabel ||= dateFromHeading(line) ?? '';
		}
		if (/^(?:suggestion(?: for a meeting to add)?|suggested meeting|meeting to add)\s*:/i.test(line)) {
			suggestionIndex = index;
			break;
		}

		const event = line.match(EVENT_RANGE);
		if (event) {
			const [, start, end, rawDescription] = event;
			if (/^free\b/i.test(rawDescription)) {
				gaps.push({start, end});
				continue;
			}
			let title = rawDescription.trim();
			let meta = '';
			const parenthetical = title.match(/\s+\(([^()]*)\)\s*$/);
			if (parenthetical) {
				meta = parenthetical[1].trim();
				title = title.slice(0, parenthetical.index).trim();
			} else {
				const parts = title.split(/\s+·\s+/);
				title = parts.shift()?.trim() ?? title;
				meta = parts.join(' · ');
			}
			events.push({start, end, title: plain(title), meta: plain(meta)});
			continue;
		}

		if (/\b(?:free|gaps?|open)\b/i.test(line)) {
			for (const match of line.matchAll(ANY_RANGE)) gaps.push({start: match[1], end: match[2]});
		}
	}

	if (!sawScheduleCue || events.length < 2) return null;
	const uniqueGaps = gaps.filter(
		(gap, index, all) =>
			all.findIndex((candidate) => candidate.start === gap.start && candidate.end === gap.end) === index,
	);
	const suggestion = suggestionIndex >= 0 ? suggestionFromText(lines.slice(suggestionIndex).join('\n').trim()) : null;
	const minimumDuration = suggestion?.duration ? Number(suggestion.duration.match(/\d+/)?.[0] ?? 0) : 0;
	const proposed = uniqueGaps.find((gap) => rangeMinutes(gap.start, gap.end) >= minimumDuration);
	return {
		dateLabel: dateLabel || 'Your schedule',
		events,
		gaps: uniqueGaps,
		suggestion,
		proposedStart: proposed?.start ?? null,
	};
}
